import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "The emotion-thought-behavior cycle demonstrates that:",
    options: [
      "Your emotions directly control your thoughts",
      "Your thoughts influence your emotions, which influence your behavior",
      "Behavior is independent of thoughts and emotions",
      "Only external events determine your emotions"
    ],
    correctAnswer: 1,
    explanation: "Your thoughts are the starting point. They create emotional responses, which in turn influence your behavior. By changing your thoughts, you can change the entire cycle."
  },
  {
    question: "What is the first step to breaking unhelpful emotional patterns?",
    options: [
      "Force yourself to think positively",
      "Become aware of your thoughts without judgment",
      "Avoid situations that trigger emotions",
      "Suppress your negative feelings"
    ],
    correctAnswer: 1,
    explanation: "Awareness is the foundation. You can't change what you don't notice. Observing your thoughts without judgment creates the space needed for change."
  },
  {
    question: "All-or-nothing thinking is problematic because:",
    options: [
      "It accurately reflects reality",
      "It prevents you from seeing nuance and middle ground",
      "It makes decision-making easier",
      "It has no impact on emotions"
    ],
    correctAnswer: 1,
    explanation: "All-or-nothing thinking creates unnecessary emotional distress. Most situations exist in shades of gray, not just black and white."
  },
  {
    question: "When you experience catastrophizing, a helpful response is to:",
    options: [
      "Accept that the worst will definitely happen",
      "Ask: 'What is the evidence for and against this thought?'",
      "Never think about potential problems",
      "Trust your initial fear completely"
    ],
    correctAnswer: 1,
    explanation: "Catastrophizing distorts reality. Examining evidence helps you see that your feared outcome is usually less likely than your mind suggests."
  },
  {
    question: "Which of the following best demonstrates the connection between thoughts, emotions, and behavior?",
    options: [
      "You feel sad, so you think negatively",
      "You think 'I'm capable,' feel confident, then take action on your goals",
      "Your behavior determines your thoughts",
      "Emotions and thoughts are completely separate"
    ],
    correctAnswer: 1,
    explanation: "This example shows the correct sequence: thought first, emotion follows, then behavior. Positive, empowering thoughts lead to better outcomes."
  }
];

const EmotioPsychoRationalModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('emotioPsychoRationalM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);
  
    React.useEffect(() => {
      if (responses[currentQuestion] !== undefined) {
        setSelectedAnswer(responses[currentQuestion]);
        setShowExplanation(true);
      } else {
        setSelectedAnswer(null);
        setShowExplanation(false);
      }
    }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/emotio-psycho-rational-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 1 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                How emotions, thoughts, and behavior are connected
              </li>
              <li>
                Common unhelpful thought patterns
              </li>
              <li>
                How to recognize and work with your emotions
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue practicing emotional awareness in your daily life!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Emotio-Psycho-Rational Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left emotio-psycho-rational-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Understanding Emotio-Psycho-Rational Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about emotio-psycho-rational wellness.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>
            
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule1QuizPage;


